/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation as useTranslationUntyped } from 'react-i18next';
import { apiService } from '../../services/api';
import { normalizeApiError } from '../../types/api-errors';
import { useAuthContext } from '../../contexts/AuthContext';
import styles from './VerifyEmail.module.css';

const useTranslation = useTranslationUntyped as any;

type Status = 'verifying' | 'success' | 'invalid' | 'expired' | 'locked' | 'error';

/**
 * AB#1612 Phase 1B — verify-email landing page.
 *
 * Hit by users clicking the verification link from their email:
 *   {frontendBaseUrl}/verify-email?email=<urlencoded>&token=<urlencoded>
 *
 * Reads `email` + `token` from query params, calls
 * POST /v2/auth/verify-email, then:
 *  - On 200: marks AuthContext.markEmailVerified(), shows success state
 *    with "Continue to dashboard" CTA
 *  - On 400: token invalid or expired — shows error with "Request a new
 *    link" CTA (only useful if user is logged in; otherwise they need to
 *    log in first to trigger Resend from the banner)
 *  - On 423: too many failed attempts — shows lockout state
 *
 * The endpoint does NOT require auth (per BE design: anyone clicking the
 * link in an email can verify, even from a fresh browser). After verify,
 * if the user happens to be logged in, the banner clears immediately;
 * if not, they log in normally and the next login response surfaces
 * emailVerificationRequired=false.
 */
const VerifyEmail: React.FC = () => {
    const { t } = useTranslation('auth');
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { markEmailVerified, isAuthenticated } = useAuthContext();

    const [status, setStatus] = useState<Status>('verifying');
    const [errorDetail, setErrorDetail] = useState<string | undefined>(undefined);
    // Guard against React StrictMode double-mount in dev firing the
    // verify-email POST twice (which would 400 the second call).
    const calledRef = useRef(false);

    useEffect(() => {
        if (calledRef.current) return;
        calledRef.current = true;

        const email = searchParams.get('email')?.trim() ?? '';
        const token = searchParams.get('token')?.trim() ?? '';

        if (!email || !token) {
            setStatus('invalid');
            setErrorDetail(t('signup.verifyEmail.errors.missingParams'));
            return;
        }

        (async () => {
            try {
                const res = await apiService.verifyEmail({ email, token });
                if (res?.emailVerified) {
                    markEmailVerified();
                    setStatus('success');
                } else {
                    setStatus('error');
                    setErrorDetail(res?.message);
                }
            } catch (err: any) {
                // AB#1919 (WS-5b) — status + detail via the single normalizer
                // (status prefers 7807 `status`; detail prefers 7807 `detail`).
                const appError = normalizeApiError(err);
                const httpStatus = appError.status;
                if (httpStatus === 400) {
                    setStatus('invalid');
                } else if (httpStatus === 423) {
                    setStatus('locked');
                } else if (httpStatus === 410) {
                    setStatus('expired');
                } else {
                    setStatus('error');
                    setErrorDetail(appError.detail || undefined);
                }
            }
        })();
    }, [searchParams, markEmailVerified, t]);

    const onContinue = () => {
        navigate(isAuthenticated ? '/' : '/form/login', { replace: true });
    };

    return (
        <div className={styles.page}>
            <div className={styles.card}>
                {status === 'verifying' && (
                    <>
                        <div className={styles.spinner} aria-hidden="true" />
                        <h1 className={styles.title}>{t('signup.verifyEmail.verifying.title')}</h1>
                        <p className={styles.body}>{t('signup.verifyEmail.verifying.body')}</p>
                    </>
                )}

                {status === 'success' && (
                    <>
                        <div className={`${styles.icon} ${styles.iconSuccess}`} aria-hidden="true">
                            <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                                <path
                                    d="M5 13l4 4L19 7"
                                    stroke="currentColor"
                                    strokeWidth="2.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </div>
                        <h1 className={styles.title}>{t('signup.verifyEmail.success.title')}</h1>
                        <p className={styles.body}>{t('signup.verifyEmail.success.body')}</p>
                        <button type="button" className={styles.primaryBtn} onClick={onContinue}>
                            {isAuthenticated
                                ? t('signup.verifyEmail.success.continueToDashboard')
                                : t('signup.verifyEmail.success.continueToLogin')}
                        </button>
                    </>
                )}

                {(status === 'invalid' || status === 'expired') && (
                    <>
                        <div className={`${styles.icon} ${styles.iconError}`} aria-hidden="true">
                            <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                                <path
                                    d="M12 9v4m0 4h.01M5.07 19h13.86c1.54 0 2.5-1.67 1.73-3L13.73 4a2 2 0 0 0-3.46 0L3.34 16c-.77 1.33.19 3 1.73 3z"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </div>
                        <h1 className={styles.title}>
                            {status === 'expired'
                                ? t('signup.verifyEmail.expired.title')
                                : t('signup.verifyEmail.invalid.title')}
                        </h1>
                        <p className={styles.body}>
                            {status === 'expired'
                                ? t('signup.verifyEmail.expired.body')
                                : (errorDetail ?? t('signup.verifyEmail.invalid.body'))}
                        </p>
                        <button type="button" className={styles.primaryBtn} onClick={onContinue}>
                            {isAuthenticated
                                ? t('signup.verifyEmail.actions.requestNewLink')
                                : t('signup.verifyEmail.actions.signIn')}
                        </button>
                    </>
                )}

                {status === 'locked' && (
                    <>
                        <div className={`${styles.icon} ${styles.iconError}`} aria-hidden="true">
                            <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                                <path
                                    d="M12 1a4 4 0 0 0-4 4v3H6a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-9a2 2 0 0 0-2-2h-2V5a4 4 0 0 0-4-4zm-2 7V5a2 2 0 1 1 4 0v3h-4z"
                                    fill="currentColor"
                                />
                            </svg>
                        </div>
                        <h1 className={styles.title}>{t('signup.verifyEmail.locked.title')}</h1>
                        <p className={styles.body}>{t('signup.verifyEmail.locked.body')}</p>
                        <button type="button" className={styles.primaryBtn} onClick={onContinue}>
                            {t('signup.verifyEmail.actions.signIn')}
                        </button>
                    </>
                )}

                {status === 'error' && (
                    <>
                        <div className={`${styles.icon} ${styles.iconError}`} aria-hidden="true">
                            <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                                <path
                                    d="M12 9v4m0 4h.01M5.07 19h13.86c1.54 0 2.5-1.67 1.73-3L13.73 4a2 2 0 0 0-3.46 0L3.34 16c-.77 1.33.19 3 1.73 3z"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </div>
                        <h1 className={styles.title}>{t('signup.verifyEmail.error.title')}</h1>
                        <p className={styles.body}>{errorDetail ?? t('signup.verifyEmail.error.body')}</p>
                        <button type="button" className={styles.primaryBtn} onClick={onContinue}>
                            {isAuthenticated
                                ? t('signup.verifyEmail.actions.continue')
                                : t('signup.verifyEmail.actions.signIn')}
                        </button>
                    </>
                )}
            </div>
        </div>
    );
};

export default VerifyEmail;
